import React from 'react'
import { ExternalLink } from 'lucide-react'

/** Open this location in the full Google Maps app / tab (no API key). */
export function googleMapsSearchUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`
}

/**
 * Embedded map preview using Google’s public embed endpoint (no API key).
 * If the iframe is blocked by the browser, the “Open in Google Maps” link still works.
 */
export function googleMapsEmbedUrl(lat: number, lng: number, zoom = 16): string {
  const q = `${lat},${lng}`
  return `https://www.google.com/maps?q=${encodeURIComponent(q)}&z=${zoom}&output=embed`
}

type Props = {
  latitude: number
  longitude: number
  /** Shown above the map and as iframe title for a11y */
  label?: string
  className?: string
  /** Default 192 — compact card; use ~220 in dialogs */
  heightPx?: number
}

export function PlaceMapPreview({
  latitude,
  longitude,
  label,
  className = '',
  heightPx = 192,
}: Props): React.JSX.Element {
  const href = googleMapsSearchUrl(latitude, longitude)
  const src = googleMapsEmbedUrl(latitude, longitude)

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center justify-between gap-2">
        {label ? (
          <span className="text-xs font-medium text-secondary truncate">{label}</span>
        ) : (
          <span className="text-xs text-tertiary">Map preview</span>
        )}
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-brand hover:underline shrink-0"
        >
          <ExternalLink className="h-3 w-3" />
          Open in Google Maps
        </a>
      </div>
      <div
        className="relative w-full overflow-hidden rounded-lg border border-soft bg-surface-alt"
        style={{ height: heightPx }}
      >
        <iframe
          title={label ? `Map: ${label}` : 'Google Maps preview'}
          src={src}
          className="absolute inset-0 h-full w-full border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
    </div>
  )
}
